import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { rememberTribunalCase } from '../../lib/caseLinks';
import { hasAnyRole, M16_ROLES } from '../../lib/roles';
import type { TribunalCase, TribunalRetryResponse } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, InfoNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function TribunalCaseDetail() {
  const { id = '' } = useParams();
  const { persona } = useAuth();
  const tq = useApiQuery<TribunalCase>(`/api/v1/m16/tribunal-cases/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<TribunalRetryResponse['transmissionResult'] | null>(null);
  const canRetry = hasAnyRole(persona?.roles, M16_ROLES.LABOUR_OFFICER);

  useEffect(() => {
    if (tq.data) rememberTribunalCase(tq.data.grievanceId, tq.data.id);
  }, [tq.data]);

  if (tq.loading) return <Spinner label="Loading tribunal case…" />;
  if (tq.error) return <ErrorNote message={tq.error} />;
  if (!tq.data) return <ErrorNote message="Not found" />;
  const t = tq.data;

  const retry = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<TribunalRetryResponse>(`/api/v1/m16/tribunal-cases/${t.id}/retry-transmission`);
      setLastResult(res.transmissionResult);
      tq.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Retry failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader title={t.certRefNo} subtitle="Employment tribunal e-filing bundle" actions={<StatusBadge status={t.filingStatus} />} />

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Bundle summary</h2>
          <p className="whitespace-pre-wrap text-sm text-slate-700">{t.bundleSummary}</p>

          <div className="mt-4 border-t border-slate-100 pt-3">
            <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Bundle documents ({t.bundleDocs.length})</div>
            {t.bundleDocs.length === 0 ? (
              <p className="text-xs text-slate-400">No documents attached.</p>
            ) : (
              <ul className="space-y-1">
                {t.bundleDocs.map((d, i) => (
                  <li key={i} className="font-mono text-xs text-slate-500">
                    {d.label ?? d.docRef}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {t.grievance ? (
            <div className="mt-4 border-t border-slate-100 pt-3 text-xs">
              <Link className="font-semibold text-brand-600 hover:underline" to={`/grievances/${t.grievance.id}`}>
                View originating grievance →
              </Link>
            </div>
          ) : null}
        </div>

        <div className="space-y-4">
          <div className="card p-4 text-sm">
            <h2 className="mb-2 text-xs font-bold uppercase text-slate-500">M37 transmission</h2>
            <dl className="space-y-1.5">
              <div className="flex justify-between">
                <dt className="text-slate-500">M37 case no.</dt>
                <dd className="font-mono text-slate-800">{t.m37CaseNumber ?? '—'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Transmitted</dt>
                <dd className="text-slate-800">{t.transmittedAt ? formatDateTime(t.transmittedAt) : '—'}</dd>
              </div>
            </dl>
          </div>

          {t.filingStatus === 'PENDING_TRANSMISSION' ? (
            <div className="card p-4">
              <InfoNote message="No live M37 service-to-service intake contract exists yet — see the build report. Retrying will honestly continue to report PENDING_TRANSMISSION until that seam is wired." />
              {canRetry ? (
                <>
                  {error ? (
                    <div className="mt-2">
                      <ErrorNote message={error} />
                    </div>
                  ) : null}
                  {lastResult ? (
                    <div className="mt-2 rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-600">Last attempt: {lastResult.status}</div>
                  ) : null}
                  <button className="btn-secondary mt-3 w-full" onClick={retry} disabled={busy}>
                    {busy ? 'Retrying…' : 'Retry transmission'}
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
